import { notification } from "antd";
import { Dispatch } from "redux";

import { ConfigurableReportsAxios } from "../Contracts/Axios/ConfigurableReportsAxios";
import { IConfigurableReport } from "../Contracts/IConfigurableReport";
import { fixGeoJsonGeometry, getDataFromRadiusString } from "../Logic/ConfigurableReportLogic";
import { IAnalysisItem } from "../Models/IAnalysisItem";
import {
    ADD_RUNNING_CONFIG_REPORT_TO_LIST,
    DELETE_CONFIG_REPORT_SUCCESS,
    GET_CONFIG_REPORTS_SUCCESS,
    SET_CR_MODAL_VISIBILITY,
    SET_CR_SELECTED_ITEMS,
    SET_LOADING_CONFIG_REPORTS,
} from "./Types";

export interface IScoutReportJob {
    title: string;
    latitude?: number;
    longitude?: number;
    params: Array<{
        key: string;
        value: any;
    }>;
}

export interface IDemReportRequest {
    templateId: string;
    reportName: string;
    revision?: number;
    point?: {
        x: number;
        y: number;
    };
    radiusMiles?: number;
    radiusKm?: number;
    radiusMinutes?: number;
    customPolygon?: string;
    territoryId?: number;
    geographyId?: number;
}

export interface ICleanedJobArea {
    title: string;
    point?: {
        x: number;
        y: number;
    };
    radiusMiles?: number;
    radiusKm?: number;
    radiusMinutes?: number;
    customPolygon?: string;
    territoryId?: number;
    geographyId?: number;
}

const setLoadingConfigReports = (loading: boolean) => ({
    type: SET_LOADING_CONFIG_REPORTS,
    data: loading,
});

const getConfigReportsSuccess = (data: any) => ({
    type: GET_CONFIG_REPORTS_SUCCESS,
    data: data,
});

const deleteConfigReportSuccess = (reportId: number) => ({
    type: DELETE_CONFIG_REPORT_SUCCESS,
    data: reportId,
});

export const addRunningConfigurableReport = () => {
    return (dispatch: Dispatch<any>): void => {
        dispatch({ type: ADD_RUNNING_CONFIG_REPORT_TO_LIST });
    };
};

export const setCrModalVisibility = (showCrModalVisibility: boolean): Record<string, unknown> => ({
    type: SET_CR_MODAL_VISIBILITY,
    data: showCrModalVisibility,
});

// Extracts the relevant data from the Scout report job and returns a cleaned object
const extractAndCleanJob = (job: IScoutReportJob): ICleanedJobArea => {
    // Start with a base object containing only the parameters we care about.
    let baseReportObj: ICleanedJobArea = {
        title: job.title,
        territoryId: null,
        geographyId: null,
        customPolygon: null,
        radiusKm: null,
        radiusMiles: null,
        radiusMinutes: null,
        point: null,
    };
  
    // Find the values we need from the report parameters.
    const radiusMilesStr = job.params.find(param => param.key === "RADII_MILES")?.value;
    const reportType = job.params.find(param => param.key === "TYPE")?.value;
    const spatialObject = job.params.find(param => param.key === "SPATIALOBJECT")?.value;
    
    // Process the spatialObject, if present, by cleaning/fixing its geometry.
    let transformedSpatialObject = spatialObject;
    if (spatialObject) {
        try {
            transformedSpatialObject = fixGeoJsonGeometry(spatialObject);
        } catch (error) {
        // Report an error and return early if the GeoJSON is invalid.
            notification.error({
                message: "Invalid GeoJSON",
                description: error.message,
                duration: 3,
            });
            return;
        }
    }
    
    // Depending on the report type, update baseReport with the appropriate data.
    switch (reportType) {
        case "DRIVETIME":
            // For drivetime, we use the (cleaned) GeoJSON as a custom polygon.
            baseReportObj.customPolygon = JSON.stringify(transformedSpatialObject);
            break;
        // This is actually an enum value correlating to circle/ring radius, but it's over in the angular side...
        case 6:
            baseReportObj = {
                ...baseReportObj,
                // For ring radius, extract location data (like radius and point) from the radius string.
                ...getDataFromRadiusString(radiusMilesStr),
            };
            break;
        case "POLYGON":
            // When the report type is POLYGON, extract the geography ID.
            baseReportObj.geographyId = job.params.find(param => param.key === "ID")?.value;
            break;
        case "CUSTOM_TERRITORY":
            // For custom territories, extract the territory ID.
            baseReportObj.territoryId = job.params.find(param => param.key === "ID")?.value;
            break;
        default:
            // If the type isn't explicitly handled, attempt to infer:
            // - Use the cleaned GeoJSON as customPolygon if available, or
            // - Use the radiusMiles string data.
            if (transformedSpatialObject) {
                baseReportObj.customPolygon = JSON.stringify(transformedSpatialObject);
            } else if (radiusMilesStr) {
                baseReportObj = {
                    ...baseReportObj,
                    ...getDataFromRadiusString(radiusMilesStr),
                };
            } else {
                throw new Error("Invalid Demographic Report Configuration");
            }
    }
    
    return baseReportObj;
};

// Assigns the selected report items to the state for use in the Configurable Reports Creation modal
export const setCrSelectedItems = (selectedItems: null | string): Record<string, unknown> => {
    // Parse the string received from the Angular side, and extract the relevant data
    const cleanedSelectedItems = selectedItems ? JSON.parse(selectedItems).map((selectedItem) => extractAndCleanJob(selectedItem)) : null;
    return {
        type: SET_CR_SELECTED_ITEMS,
        data: JSON.stringify(cleanedSelectedItems),
    };
};

const configReportsAxios = new ConfigurableReportsAxios();

// Used to populate the reports list with the user's configurable reports
export const getConfigurableReports = (startDate: Date, endDate: Date, collaborate?: boolean) => {
    return (dispatch: Dispatch<any>): void => {
        dispatch(setLoadingConfigReports(true));
        configReportsAxios.GetConfigurableReports(collaborate).then((response: IConfigurableReport[]) => {
            // Ensure startDate and endDate are valid Date objects
            if (!startDate || !endDate) {
                throw new Error("Invalid start or end date");
            }
            
            const startTimeAsUTC = new Date(new Date(startDate.setHours(0, 0, 0, 0)).toUTCString());
            const endTimeAsUTC = new Date(new Date(endDate.setHours(23, 59, 59, 999)).toUTCString());
            // Filter out reports that are not within the date range
            const filteredReports = response.filter((report) => {
                const reportTime = new Date(report.startDateTime + "Z");
                return reportTime >= startTimeAsUTC && reportTime <= endTimeAsUTC;
            });

            dispatch(getConfigReportsSuccess(filteredReports));
        }).catch((error: string) => {
            notification.error({
                message: "There was an error fetching configurable reports",
                description: error,
                duration: null,
            });
        }).finally(() => {
            dispatch(setLoadingConfigReports(false));
        });
    };
};

export const deleteConfigurableReport = (report: IAnalysisItem) => {
    return (dispatch: Dispatch<any>): void => {
        dispatch(setLoadingConfigReports(true));
        const reportIdAsString = String(report.id);
        configReportsAxios.DeleteConfigurableReport(reportIdAsString).then((success) => {
            if (!success) {
                notification.error({
                    message: "There was an error deleting your report",
                    description: "",
                    duration: null,
                });
            } else {
                notification.success({
                    message: "Success",
                    description: "Configurable Report deleted successfully",
                    duration: 3,
                });
                dispatch(deleteConfigReportSuccess(report.id));
            }
        }).catch((error: string) => {
            notification.error({
                message: "There was an error deleting your report",
                description: error,
                duration: null,
            });
        }).finally(() => {
            dispatch(setLoadingConfigReports(false));
        });;
    };
};
